import { ApexChart } from 'ng-apexcharts';
import { clearApexChartInteraction, clearApexChartSelection } from 'shared/types/chart.config';

interface DashboardApexRuntimeContext {
  w?: {
    dom?: {
      baseEl?: HTMLElement;
    };
  };
}

export const DASHBOARD_CHART_INTERACTION_EVENTS: NonNullable<ApexChart['events']> = {
  mouseLeave: (_event, chartContext) => {
    clearDashboardChartInteraction(chartContext);
  },
  click: (_event, chartContext) => {
    clearDashboardChartKeyboardFocus(chartContext);
  },
  dataPointMouseLeave: (_event, chartContext) => {
    clearDashboardDataPointInteraction(chartContext);
  },
  dataPointSelection: (_event, chartContext) => {
    clearApexChartSelection(chartContext);
  }
};

export const DASHBOARD_EARNINGS_GROWTH_CHART_EVENTS: NonNullable<ApexChart['events']> = {
  ...DASHBOARD_CHART_INTERACTION_EVENTS,
  click: (_event, chartContext) => {
    clearDashboardChartInteraction(chartContext);
  },
  mouseMove: (event, chartContext) => {
    positionCrosshairAtHoveredCategory(event, chartContext, 6);
    scheduleTooltipAtPointer(event, chartContext);
  }
};

function clearDashboardChartInteraction(chartContext: DashboardApexRuntimeContext | null | undefined): void {
  clearApexChartInteraction(chartContext);
  clearDashboardChartKeyboardFocus(chartContext);
}

function clearDashboardDataPointInteraction(chartContext: DashboardApexRuntimeContext | null | undefined): void {
  clearApexChartSelection(chartContext);
  clearDashboardChartKeyboardFocus(chartContext);
}

function clearDashboardChartKeyboardFocus(chartContext: DashboardApexRuntimeContext | null | undefined): void {
  chartContext?.w?.dom?.baseEl
    ?.querySelectorAll('.apexcharts-keyboard-focused')
    .forEach(element => element.classList.remove('apexcharts-keyboard-focused'));
}

function positionCrosshairAtHoveredCategory(
  event: MouseEvent | TouchEvent,
  chartContext: DashboardApexRuntimeContext | null | undefined,
  categoryCount: number
): void {
  const baseElement = chartContext?.w?.dom?.baseEl;
  const gridElement = baseElement?.querySelector<SVGElement>('.apexcharts-grid');
  const crosshairElement = baseElement?.querySelector<SVGElement>('.apexcharts-xcrosshairs');
  const clientX = 'touches' in event ? event.touches[0]?.clientX : event.clientX;

  if (!gridElement || !crosshairElement || clientX === undefined) {
    return;
  }

  const gridBounds = gridElement.getBoundingClientRect();

  if (gridBounds.width <= 0) {
    return;
  }

  const categoryWidth = gridBounds.width / categoryCount;
  const pointerX = Math.min(Math.max(clientX - gridBounds.left, 0), gridBounds.width - Number.EPSILON);
  const categoryIndex = Math.floor(pointerX / categoryWidth);
  const crosshairX = categoryIndex * categoryWidth;

  crosshairElement.setAttribute('width', String(categoryWidth));
  crosshairElement.setAttribute('x', String(crosshairX));
  crosshairElement.setAttribute('x1', String(crosshairX));
  crosshairElement.setAttribute('x2', String(crosshairX));
}

function scheduleTooltipAtPointer(
  event: MouseEvent | TouchEvent,
  chartContext: DashboardApexRuntimeContext | null | undefined
): void {
  const clientX = 'touches' in event ? event.touches[0]?.clientX : event.clientX;

  if (clientX === undefined) {
    return;
  }

  queueMicrotask(() => positionTooltipAtPointer(clientX, chartContext));
}

function positionTooltipAtPointer(clientX: number, chartContext: DashboardApexRuntimeContext | null | undefined): void {
  const baseElement = chartContext?.w?.dom?.baseEl;
  const gridElement = baseElement?.querySelector<SVGElement>('.apexcharts-grid');
  const tooltipElement = baseElement?.querySelector<HTMLElement>('.apexcharts-tooltip.apexcharts-active');

  if (!baseElement || !gridElement || !tooltipElement) {
    return;
  }

  const baseBounds = baseElement.getBoundingClientRect();
  const gridBounds = gridElement.getBoundingClientRect();
  const tooltipBounds = tooltipElement.getBoundingClientRect();

  if (baseBounds.width <= 0 || gridBounds.width <= 0 || tooltipBounds.width <= 0) {
    return;
  }

  const pointerX = Math.min(Math.max(clientX, gridBounds.left), gridBounds.right - Number.EPSILON);
  const gridCenterX = gridBounds.left + gridBounds.width / 2;
  const preferredLeft =
    pointerX > gridCenterX ? pointerX - baseBounds.left - tooltipBounds.width : pointerX - baseBounds.left;
  const maxLeft = Math.max(baseBounds.width - tooltipBounds.width, 0);
  const tooltipLeft = Math.min(Math.max(preferredLeft, 0), maxLeft);

  tooltipElement.style.left = `${tooltipLeft}px`;
}
